"use client";

import {
  createContext,
  useContext,
  useMemo,
  type ReactNode,
} from "react";
import type { PaginationVariant } from "./variants";

export interface PaginationContextValue {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  disabled: boolean;
  variant: PaginationVariant;
  atFirst: boolean;
  atLast: boolean;
}

const PaginationContext = createContext<PaginationContextValue | null>(null);

export function usePaginationContext(): PaginationContextValue {
  const ctx = useContext(PaginationContext);
  if (!ctx) {
    throw new Error("Pagination components must be used within <Pagination.Root>");
  }
  return ctx;
}

export interface PaginationRootProps {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  /** Extra disable (e.g. placeholder query data). */
  disabled?: boolean;
  variant?: PaginationVariant;
  children: ReactNode;
  className?: string;
}

export function PaginationRoot({
  page,
  totalPages,
  onPageChange,
  disabled = false,
  variant = "search",
  children,
}: PaginationRootProps) {
  const safeTotal = Math.max(1, totalPages);
  const safePage = Math.min(Math.max(1, page), safeTotal);

  const value = useMemo<PaginationContextValue>(
    () => ({
      page: safePage,
      totalPages: safeTotal,
      onPageChange,
      disabled,
      variant,
      atFirst: safePage <= 1,
      atLast: safePage >= safeTotal,
    }),
    [safePage, safeTotal, onPageChange, disabled, variant],
  );

  return (
    <PaginationContext.Provider value={value}>
      {children}
    </PaginationContext.Provider>
  );
}
